import { InteractionRequiredAuthError } from '@azure/msal-browser'

import { tokenRequest } from '@/authConfig'
import { msalService } from '@/common/services/MsalService'

/**
 * Get access token using centralized MsalService pattern.
 * Uses the singleton MsalService for consistent initialization
 * across all authentication flows.
 *
 * @returns {Promise<{token: string|null}>} Access token
 */
const getAccessToken = async () => {
    // Use centralized MsalService (singleton pattern)
    let msalInstance
    try {
        msalInstance = await msalService.initialize()
    } catch (error) {
        console.error('[getAccessToken] Failed to initialize MSAL via MsalService:', error)
        return { token: null }
    }

    try {
        const tokenResponse = await msalInstance.acquireTokenSilent(tokenRequest)
        const token = tokenResponse?.accessToken ?? null

        console.debug('[getAccessToken] Token acquired successfully via silent flow')
        return { token }
    } catch (error) {
        console.error('[getAccessToken] Error acquiring token:', error.message)

        if (error instanceof InteractionRequiredAuthError) {
            // Do NOT redirect to B2C via acquireTokenRedirect — the user may have been
            // migrated to Kinde, in which case the stale B2C cache would send them to
            // the wrong login page (bypassing /signout, purge, and email-first detection).
            // Instead, purge all auth state and redirect to /signout, which routes the
            // user through the email-first flow to their current provider.
            console.warn('[getAccessToken] Interaction required — purging auth state and redirecting to /signout')
            const { purgeAuthState } = await import('@/common/helpers/auth/purgeAuthState')
            await purgeAuthState()
            const { SIGNOUT_PATHNAME } = await import('@/common/utils/Paths/Paths')
            window.location.href = SIGNOUT_PATHNAME
        }

        return { token: null }
    }
}

export { getAccessToken }
